import { useState } from 'react';
import supabase from '@/lib/supabaseClient';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    service: 'fulfillment'
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailStatus('sending');

    try {
      // 1. Daten in Supabase speichern
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert([{
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          phone: formData.phone || null,
          service: formData.service,
          message: formData.message,
        }]);

      if (dbError) {
        console.error('Supabase Fehler:', dbError.message, dbError.details, dbError.hint, dbError.code);
        throw new Error(dbError.message);
      }

      // 2. E-Mail über Edge Function senden (mit supabase.functions.invoke)
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: { record: formData },
      });

      if (emailError) {
        console.error('E-Mail Fehler:', emailError);
        setEmailStatus('error');
      } else {
        console.log('E-Mail gesendet:', emailData);
        setEmailStatus('sent');
      }

      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        service: 'fulfillment'
      });
      setTimeout(() => {
        setShowSuccess(false);
        setEmailStatus('idle');
      }, 8000);
    } catch (err) {
      console.error('Formular-Fehler:', err);
      setEmailStatus('error');
      alert('Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut oder rufen Sie uns an: +49 2642 2913998');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gray-100">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Kontakt aufnehmen
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Lassen Sie uns gemeinsam besprechen, wie wir Ihr Fulfillment mit Herz und Hand 
            zu einem besonderen Erlebnis für Ihre Kunden machen können.
          </p>
        </div>

        {showSuccess && (
          <div className="max-w-2xl mx-auto mb-8 bg-white border-2 border-black rounded-xl p-6 shadow-lg">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-black mb-2">
                  Vielen Dank für Ihre Nachricht!
                </h3>
                <p className="text-gray-700 mb-3">
                  Wir haben Ihre Anfrage erhalten und melden uns innerhalb von 24 Stunden bei Ihnen.
                </p>
                {emailStatus === 'sent' && (
                  <div className="bg-green-50 border border-green-400 p-3 rounded-lg mb-3">
                    <p className="text-sm text-green-800 flex items-center">
                      <i className="ri-mail-send-line mr-2"></i>
                      Bestätigungs-E-Mail wurde erfolgreich versandt.
                    </p>
                  </div>
                )}
                {emailStatus === 'error' && (
                  <div className="bg-amber-50 border border-amber-400 p-3 rounded-lg mb-3">
                    <p className="text-sm text-amber-800 flex items-center">
                      <i className="ri-error-warning-line mr-2"></i>
                      Ihre Nachricht wurde gespeichert, aber die E-Mail-Benachrichtigung konnte nicht versandt werden. Wir melden uns trotzdem bei Ihnen!
                    </p>
                  </div>
                )}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Dringende Anfragen?</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Rufen Sie uns gerne direkt an: <strong>+49 2642 2913998</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-black mb-6">
              Sprechen Sie mit uns
            </h3>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-black text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">handgepackt eGbR</h4>
                  <p className="text-gray-600">
                    Römerstr. 1<br />
                    53424 Remagen, Deutschland
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-black text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Telefon</h4>
                  <p className="text-gray-600">+49 2642 2913998</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-printer-line text-black text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Telefax</h4>
                  <p className="text-gray-600">+49 2642 2913999</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-black text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">E-Mail</h4>
                  <p className="text-gray-600">hallo@handgepackt.de</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-black text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Öffnungszeiten</h4>
                  <p className="text-gray-600">
                    Mo-Fr: 8:00 - 18:00 Uhr<br />
                    Sa: 9:00 - 14:00 Uhr
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h4 className="font-bold text-black mb-3">Warum handgepackt wählen?</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-black mr-2"></i>
                  <span className="text-sm">Traditionelle Handpackkunst</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-black mr-2"></i>
                  <span className="text-sm">Persönlicher Service</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-black mr-2"></i>
                  <span className="text-sm">Nachhaltige Verpackung</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-black mr-2"></i>
                  <span className="text-sm">Schnelle Bearbeitung</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-black mr-2"></i>
                  <span className="text-sm">Werktäglicher Versand</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
                    placeholder="Ihr Name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Unternehmen
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
                    placeholder="Ihr Unternehmen"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Gewünschte Leistung
                </label>
                <div className="relative">
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm pr-8 appearance-none cursor-pointer"
                  >
                    <option value="fulfillment">Komplettes Fulfillment</option>
                    <option value="packaging">Nur Verpackung</option>
                    <option value="storage">Nur Lagerung</option>
                    <option value="shipping">Nur Versand</option>
                    <option value="consultation">Beratung</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nachricht *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm resize-vertical"
                  placeholder="Erzählen Sie uns von Ihrem Projekt..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 Zeichen
                </div>
              </div>

              <button
                type="submit"
                disabled={emailStatus === 'sending'}
                className="w-full bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {emailStatus === 'sending' ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Wird gesendet...
                  </span>
                ) : (
                  'Nachricht senden'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}